import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Divider, Stack } from "@mui/material";
import { FormTextField } from "UI/FormTextField";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { saveBranding } from "store/reducers/brandingReducer";
import { GlobalContext } from "store/globalContext";
import { Title } from "SC";

const HEX_COLOR_PATTERN = /^#([A-Fa-f0-9]{6})$/;

const hexColorValidation = {
  pattern: {
    value: HEX_COLOR_PATTERN,
    message: "Must be a hex color like #1173F5",
  },
};

export const BrandingSettings = () => {
  const dispatch = useAppDispatch();
  const { showWarning } = useContext(GlobalContext);
  const branding = useAppSelector((state) => state.branding);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm({
    defaultValues: {
      companyName: branding.companyName || "",
      primaryColor: branding.primaryColor || "#1173F5",
      secondaryColor: branding.secondaryColor || "#284A70",
      logoUrl: branding.logoUrl || "",
    },
  });

  useEffect(() => {
    reset({
      companyName: branding.companyName || "",
      primaryColor: branding.primaryColor || "#1173F5",
      secondaryColor: branding.secondaryColor || "#284A70",
      logoUrl: branding.logoUrl || "",
    });
  }, [branding.companyName, branding.primaryColor, branding.secondaryColor, branding.logoUrl, reset]);

  const primaryColor = watch("primaryColor");
  const secondaryColor = watch("secondaryColor");
  const logoUrl = watch("logoUrl");
  const companyName = watch("companyName");

  const onSubmit = async (data) => {
    const result = await dispatch(saveBranding(data));
    if (result?.meta?.requestStatus === "fulfilled") {
      showWarning({
        title: "Branding Updated",
        content: "Tenant branding has been successfully updated.",
      });
    } else {
      showWarning({
        title: "Update Failed",
        content: typeof result?.payload === "string" ? result.payload : "Failed to update branding.",
      });
    }
  };

  return (
    <Stack gap={3} component="form" onSubmit={handleSubmit(onSubmit)}>
      <Title>Branding & White-Label</Title>
      <Stack gap={2} maxWidth={560}>
        <FormTextField
          name="companyName"
          label="Company Name"
          control={control}
          rules={{ required: "Company name is required", maxLength: { value: 255, message: "Max 255 characters" } }}
        />
        <FormTextField
          name="primaryColor"
          label="Primary Color (hex)"
          control={control}
          rules={hexColorValidation}
        />
        <FormTextField
          name="secondaryColor"
          label="Secondary Color (hex)"
          control={control}
          rules={hexColorValidation}
        />
        <FormTextField
          name="logoUrl"
          label="Logo URL"
          control={control}
          rules={{ maxLength: { value: 2048, message: "Max 2048 characters" } }}
        />
      </Stack>

      <Divider />

      <Stack gap={1}>
        <Title sx={{ fontSize: "1rem" }}>Preview</Title>
        <Stack direction="row" gap={2} alignItems="center">
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={companyName || "Logo"}
              sx={{ maxHeight: 48, maxWidth: 200, objectFit: "contain" }}
            />
          ) : null}
          <Box sx={{ fontWeight: 600 }}>{companyName}</Box>
        </Stack>
        <Stack direction="row" gap={2}>
          <Box
            sx={{
              width: 120,
              height: 48,
              borderRadius: 1,
              backgroundColor: HEX_COLOR_PATTERN.test(primaryColor) ? primaryColor : "transparent",
              border: "1px solid #ccc",
            }}
          />
          <Box
            sx={{
              width: 120,
              height: 48,
              borderRadius: 1,
              backgroundColor: HEX_COLOR_PATTERN.test(secondaryColor) ? secondaryColor : "transparent",
              border: "1px solid #ccc",
            }}
          />
        </Stack>
      </Stack>

      <Stack direction="row" gap={2}>
        <Button
          type="submit"
          variant="contained"
          disabled={!isDirty || isSubmitting}
        >
          Save Branding
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={() => reset()}
          disabled={!isDirty || isSubmitting}
        >
          Reset
        </Button>
      </Stack>
    </Stack>
  );
};
